import {
  IRobotExtracted,
  IRobotExtractionResult,
  IRobotPreview,
  IRobotPreviewConfiguration,
  IRobotSentence,
  IRobotWordClick,
  TRobotFormData,
} from '../models/interfaces/robots.interface';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { from, Observable, of, Subject } from 'rxjs';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { map, switchMap, mergeMap, toArray, startWith } from 'rxjs/operators';
import { DefaultRobotGroupConfiguration, DefaultRobotGroupConfigurationTest } from '../models/defaults/group-configuration.default';
import { TChangePageEvent } from '../models/interfaces/files.interface';
import { IFilePageAndWords, IFileProcessedWord, IFileTable } from '../models/interfaces/files.interface';
import { Calcs } from '../utilities/calculations';
import { IGroupConfiguration } from '../models/interfaces/group-keys.interface';
import { ExtractorsUtility } from '../utilities/extractors';
import { IClassificationParameters } from '../models/interfaces/classification-parameters.interface';
import { ClassificationParameters, RobotPreview } from '../models/robots.model';
import IOffsetPagination from '../models/interfaces/offset-pagination.interface';
import { EventEmitterService } from './event-emitter.service';

const robotsCacheBuster$ = new Subject<void>();
const extractionsCacheBuster$ = new Subject<void>();

@Injectable({ providedIn: 'root' })
export class RobotsService {
  constructor(private _http: HttpClient, private _eventEmitterService: EventEmitterService) {}

  public getCustomRobots(robotId: string): Observable<IClassificationParameters[]> {
    return this._http.get<IClassificationParameters[]>(`${environment.filesApiUrl}/robots/${robotId}/classifications/`);
  }

  /**
   * @returns Configurações gerais do robô, como os cliques e sentenças escolhidas.
   */
  @Cacheable({
    maxAge: 1000 * 60 * 15,
    maxCacheCount: 25,
    cacheBusterObserver: robotsCacheBuster$,
  })
  public getCustomRobot(id: string, robotId: string): Observable<IClassificationParameters> {
    return this._http.get<IClassificationParameters>(`${environment.filesApiUrl}/robots/${robotId}/classifications/${id}`).pipe(
      map((classificationParameters) => {
        if (classificationParameters) {
          classificationParameters.wordClicks?.map((wordClick) => {
            if (wordClick.polygon) {
              wordClick.polygon.boundingBox = Calcs.polygonToBoundingBox(wordClick.polygon);
            }
          });

          classificationParameters.sentences?.map((sentence) => {
            if (sentence.polygon) {
              sentence.polygon.boundingBox = Calcs.polygonToBoundingBox(sentence.polygon);
            }
          });

          classificationParameters.pages?.forEach((page) => {
            page.wordClicks?.forEach((wordClick) => {
              if (wordClick.polygon) {
                wordClick.polygon.boundingBox = Calcs.polygonToBoundingBox(wordClick.polygon);
              }
            });

            page.sentences?.forEach((sentence) => {
              if (sentence.polygon?.boundingBox) {
                sentence.polygon.boundingBox = Calcs.polygonToBoundingBox(sentence.polygon);
              }
            });
          });
          try {
            classificationParameters.state = JSON.parse(classificationParameters.state) ?? classificationParameters.state;
          } catch (error) {
            console.error(error);
          }
          return classificationParameters;
        }
        return null;
      })
    );
  }

  @CacheBuster({ cacheBusterNotifier: robotsCacheBuster$ })
  @CacheBuster({ cacheBusterNotifier: extractionsCacheBuster$ })
  public createCustomRobot(classificationParameters: ClassificationParameters): Observable<IClassificationParameters> {
    return this._http.post<ClassificationParameters>(
      `${environment.filesApiUrl}/robots/${classificationParameters.robotId}/classifications`,
      classificationParameters.toJson()
    );
  }

  @CacheBuster({ cacheBusterNotifier: robotsCacheBuster$ })
  @CacheBuster({ cacheBusterNotifier: extractionsCacheBuster$ })
  public editCustomRobot(id: string, classificationParameters: ClassificationParameters): Observable<IClassificationParameters> {
    return this._http.put<ClassificationParameters>(
      `${environment.filesApiUrl}/v2/robots/${classificationParameters.robotId}/classifications/${id}`,
      classificationParameters.toJson()
    );
  }

  @CacheBuster({ cacheBusterNotifier: robotsCacheBuster$ })
  public deleteCustomRobot(id: string, robotId: string): Observable<void> {
    return this._http.delete<void>(`${environment.filesApiUrl}/v2/robots/${robotId}/classifications/${id}`);
  }

  // eslint-disable-next-line prettier/prettier
  public updateRobotExtractionResult(fileId: string, extractionId: string, robotId: string, documentId: string, changedData: TRobotFormData): Observable<void> {
    return this._http.put<void>(
      `${environment.filesApiUrl}/robot/files/${fileId}/extractions/${extractionId}/robots/${robotId}/documents/${documentId}`,
      changedData
    );
  }

  /**
   * Busca o resultado da última extração do robô para o arquivo, listando os robôs que passaram.
   * Verifica se a última extração é mais velha do que a data de criação/edição do \
   * robô. Se for mais velho, dar um enqueue novamente e aguardar o resultado. \
   * Também verifica se não há resultado de extração, e tenta mais uma vez para \
   * verificar se não há novos robôs que pegam esse tipo de arquivo.
   * @param {string} fileId Id do arquivo
   */
  @Cacheable({ maxAge: 1000 * 60 * 15, cacheBusterObserver: extractionsCacheBuster$, maxCacheCount: 10 })
  public getLatestExtractionResultFromFile(fileId: string, robotId: string): Observable<IRobotExtractionResult> {
    return this._http.get<IRobotExtractionResult | null>(`${environment.filesApiUrl}/robot/files/${fileId}/lastextraction`).pipe(
      mergeMap((extractionResult) => {
        let robotsExtracted: Observable<IRobotExtracted>;

        if (!extractionResult) {
          robotsExtracted = this.runRobot(fileId, robotId).pipe(
            mergeMap((x) => {
              extractionResult = x;
              return from(x?.robots);
            })
          );
        } else {
          robotsExtracted = from(extractionResult?.robots);
        }

        return robotsExtracted.pipe(
          mergeMap((result) => {
            if (result) {
              return this.getCustomRobot(result.robot.id, robotId).pipe(
                map((robotResult) => Object.assign(result.robot, robotResult)),
                mergeMap((robotResult) => {
                  if (extractionResult && robotResult.updateDate && robotResult.updateDate > extractionResult.createDate) {
                    return this.runRobot(fileId, robotResult.robotId).pipe(
                      mergeMap((r) => {
                        extractionResult = r;
                        return this.getCustomRobot(robotResult.id, robotId);
                      })
                    );
                  } else {
                    return of(robotResult);
                  }
                })
              );
            } else {
              return of(null);
            }
          }),
          toArray(),
          map((items) => {
            if (extractionResult) {
              extractionResult.robots = extractionResult.robots.map((robot) => {
                robot.robot = items.find((x) => x.id === robot.robot.id);
                return robot;
              });
            }

            return extractionResult;
          })
        );
      })
    );
  }

  /**
   * Retorna a lista de configurações permitidas para um robô
   * @todo Ainda não existe Back-end para configurações de robô.
   */
  public getRobotsGroupsConfigurations(robotId: string): Observable<IGroupConfiguration[]> {
    if (environment.production) {
      return of(DefaultRobotGroupConfiguration.filter((x) => x.robotId === robotId));
    } else {
      return of(DefaultRobotGroupConfigurationTest.filter((x) => x.robotId === robotId));
    }
  }

  /**
   * Retorna as configurações de um grupo específico de configurações
   * @todo Ainda não existe Back-end para configurações de robô.
   */
  public getRobotsGroupsConfiguration(robotId: string, groupId: string): Observable<IGroupConfiguration> {
    if (environment.production) {
      return of(DefaultRobotGroupConfiguration.filter((x) => x.robotId === robotId).find((x) => x.id === groupId));
    } else {
      return of(DefaultRobotGroupConfigurationTest.filter((x) => x.robotId === robotId).find((x) => x.id === groupId));
    }
  }

  /**
   * Executa o robô para o arquivo selecionado, independentemente se já foi executado alguma vez no passado.
   */
  public runRobot(fileId: string, robotId: string): Observable<IRobotExtractionResult> {
    return this._http.get<IRobotExtractionResult>(
      `${environment.filesApiUrl}/robots/${robotId}/run/${fileId}?hasCustomRobots=true&context=classification-app`
    );
  }

  /**
   * Faz o preview do robô para um arquivo, retornando o resultado antes de precisar salvar.
   */
  public previewRobot(
    sentences: IRobotSentence[],
    wordClicks: IRobotWordClick[],
    testRobotConfigurations: IRobotPreviewConfiguration[]
  ): Observable<RobotPreview[]> {
    return this._http
      .post<IRobotPreview[]>(`${environment.filesApiUrl}/robot/preview`, {
        testRobotConfigurations:
          typeof testRobotConfigurations === 'object' && testRobotConfigurations.length !== 0 ? testRobotConfigurations : null,
        sentences,
        wordClicks,
      })
      .pipe(
        map((results) => {
          return results.map((result) => {
            return new RobotPreview(result);
          });
        })
      );
  }

  /**
   * Retorna o resultado do OCR do arquivo contendo linhas e palavras
   * @param {string} fileId
   * @returns {IFilePageAndWords[]}
   */
  public getPagesAndWords(fileId: string, defaultPagination: IOffsetPagination): Observable<IFilePageAndWords[]> {
    return this._getPagesAndWords(fileId, defaultPagination).pipe(
      switchMap((result) => {
        return this._eventEmitterService.get<TChangePageEvent>('change-pages-to-show').pipe(
          mergeMap((changePageEvent) => {
            if (changePageEvent.fileId === fileId) {
              return this._getPagesAndWords(fileId, {
                limit: changePageEvent.pagesToShow[changePageEvent.pagesToShow.length - 1] - changePageEvent.pagesToShow[0] + 1,
                offset: changePageEvent.pagesToShow[0] - 1,
              }).pipe(startWith([]));
            } else {
              return of(result);
            }
          }),
          startWith(result)
        );
      })
    );
  }

  private _getPagesAndWords(fileId: string, paginationOptions: IOffsetPagination): Observable<IFilePageAndWords[]> {
    return this._http
      .get<{ analyzeResult: { readResults: IFilePageAndWords[] } }>(
        `${environment.filesApiUrl}/cognitive/vision/v3.2/read/${fileId}/analyzeResults?limit=${paginationOptions?.limit ?? 1}&offset=${paginationOptions?.offset ?? 0}`
      )
      .pipe(
        map((result) => {
          result.analyzeResult.readResults.forEach((page, index) => {
            page.lines.forEach((line) => {
              line.words.forEach((word) => {
                word.processedValues = this._tryParseWord(word.text);
                word.pageNumber = page.page;
                word.pageIndex = index;
              });

              line.confidence = line.words.map((x) => x.confidence).reduce((a, b) => a + b, 0) / line.words.length;
              line.processedValues = this._tryParseWord(line.text);
              line.pageNumber = page.page;
              line.pageIndex = index;
            });
          });
          return result.analyzeResult.readResults;
        })
      );
  }

  /**
   * Retorna o resultado do OCR do arquivo contendo linhas, palavras e tabelas.
   * Lembre-se que, ao usar este método, estaremos pagando por cada requisição.
   * @param {string} fileId
   * @returns {IFilePageAndWords[]}
   */
  public getPagesAndWordsAndTables(fileId: string, defaultPagination: IOffsetPagination): Observable<IFilePageAndWords[]> {
    return this._getPagesAndWordsAndTables(fileId, defaultPagination).pipe(
      switchMap((result) => {
        return this._eventEmitterService.get<TChangePageEvent>('change-pages-to-show').pipe(
          mergeMap((changePageEvent) => {
            if (changePageEvent.fileId === fileId) {
              return this._getPagesAndWords(fileId, {
                limit: changePageEvent.pagesToShow[changePageEvent.pagesToShow.length - 1] - changePageEvent.pagesToShow[0] + 1,
                offset: changePageEvent.pagesToShow[0] - 1,
              }).pipe(startWith([]));
            } else {
              return of(result);
            }
          }),
          startWith(result)
        );
      })
    );
  }

  private _getPagesAndWordsAndTables(fileId: string, paginationOptions: IOffsetPagination): Observable<IFilePageAndWords[]> {
    return this._http
      .get<{ analyzeResult: { readResults: IFilePageAndWords[]; pageResults: { page: number; tables: IFileTable[] }[] } }>(
        `${environment.filesApiUrl}/cognitive/formrecognizer/v2.1/layout/${fileId}/analyzeResults?limit=${paginationOptions?.limit}&offset=${paginationOptions?.offset}`
      )
      .pipe(
        map((result) => {
          result.analyzeResult.readResults.forEach((page, index) => {
            page.lines.forEach((line) => {
              line.words.forEach((word) => {
                word.processedValues = this._tryParseWord(word.text);
                word.pageNumber = page.page;
                word.pageIndex = index;
              });

              line.confidence = line.words.map((x) => x.confidence).reduce((a, b) => a + b, 0) / line.words.length;
              line.processedValues = this._tryParseWord(line.text);
              line.pageNumber = page.page;
              line.pageIndex = index;
            });
            page.tables = result.analyzeResult.pageResults.find((x) => x.page === page.page)?.tables ?? [];
          });
          return result.analyzeResult.readResults;
        })
      );
  }

  /**
   * Limpa o cache das extrações dos arquivos
   */
  @CacheBuster({ cacheBusterNotifier: extractionsCacheBuster$ })
  public clearExtractionsCache(): Observable<void> {
    return of();
  }

  /**
   * Tenta fazer parser de um texto para os principais tipos de dados localmente.
   * @param text Texto que pode conter informações específicas
   */
  private _tryParseWord(text: string): IFileProcessedWord[] {
    const result: IFileProcessedWord[] = [];
    const parsers = [
      ExtractorsUtility.tryParseCNPJ,
      ExtractorsUtility.tryParseCPF,
      ExtractorsUtility.tryParseCurrency,
      ExtractorsUtility.tryParseDate,
      ExtractorsUtility.tryParseEntityName,
      ExtractorsUtility.tryParseBoletoBarcode,
    ];

    parsers.forEach((parser, index) => {
      const value = parser(text);

      if (value) {
        result.push({ index, ...value });
      }
    });

    return result;
  }
}
